import React, { useState, useEffect, useMemo, useRef, useCallback } from "react";
import ApiClient from "../../methods/api/apiClient";
import loader from "../../methods/loader";
import methodModel from "../../methods/methods";
import { Link, useNavigate, useParams } from "react-router-dom";
import Layout from "../../components/global/layout";
import statusModel from "../../models/status.model";
import { Tooltip } from "antd";
import FormControl from "../../components/common/FormControl";
import timezoneModel from "../../models/timezone.model";
import shared from "./shared";
import datepipeModel from "../../models/datepipemodel";
import { useSelector } from "react-redux";
import ReactQuill, { Quill } from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import { decryptId, encryptId } from "../../components/common/Encryption/encryption";
import { toast } from "react-toastify";
import Swal from 'sweetalert2';
import environment from "../../environment";

// Register inline-style size attributor so emails render font sizes without Quill CSS
const SizeStyle = Quill.import('attributors/style/size');
SizeStyle.whitelist = ['10px', '13px', '18px', '32px'];
Quill.register(SizeStyle, true);

// Register inline-style font attributor
const FontStyle = Quill.import('attributors/style/font');
Quill.register(FontStyle, true);

const DraggableLogoPreview = ({ logoUrl, settings, onUpdateSetting }) => {
    const containerRef = useRef(null);
    const [isDragging, setIsDragging] = useState(false);
    const dragOffset = useRef({ x: 0, y: 0 });

    const handleMouseMove = useCallback((e) => {
        if (!isDragging) return;
        const container = containerRef.current?.parentElement;
        if (!container) return;
        const rect = container.getBoundingClientRect();
        let newX = ((e.clientX - rect.left - dragOffset.current.x) / rect.width) * 100;
        newX = Math.max(0, Math.min(newX, 100 - (settings.size / rect.width) * 100));
        onUpdateSetting('x', Math.round(newX * 10) / 10);
    }, [isDragging, settings.size, onUpdateSetting]);

    const handleMouseUp = useCallback(() => {
        setIsDragging(false);
    }, []);

    useEffect(() => {
        if (isDragging) {
            window.addEventListener('mousemove', handleMouseMove);
            window.addEventListener('mouseup', handleMouseUp);
            return () => {
                window.removeEventListener('mousemove', handleMouseMove);
                window.removeEventListener('mouseup', handleMouseUp);
            };
        }
    }, [isDragging, handleMouseMove, handleMouseUp]);

    if (!logoUrl) return null;

    const handleMouseDown = (e) => {
        e.preventDefault();
        const container = containerRef.current?.parentElement;
        if (!container) return;
        const rect = container.getBoundingClientRect();
        const logoX = (settings.x / 100) * rect.width;
        dragOffset.current = {
            x: e.clientX - rect.left - logoX,
            y: 0
        };
        setIsDragging(true);
    };

    return (
        <div style={{ padding: '10px 0 0 0' }}>
            <div
                ref={containerRef}
                onMouseDown={handleMouseDown}
                style={{
                    display: 'block',
                    marginLeft: `${settings.x}%`,
                    cursor: isDragging ? 'grabbing' : 'grab',
                    userSelect: 'none',
                    width: 'fit-content',
                }}
            >
                <img src={logoUrl} alt="Company Logo" style={{ width: `${settings.size}px`, height: 'auto', display: 'block', pointerEvents: 'none' }} draggable={false} />
            </div>
            {settings.padding > 0 && (
                <div style={{ height: `${settings.padding}px`, width: '100%' }} />
            )}
        </div>
    );
};

const AddEdit = () => {
    const { eventid } = useParams()
    const [id, setId] = useState(null);
    useEffect(() => {
        if (eventid) {
            decryptId(eventid).then(decrypted => setId(decrypted));
        }
    }, [eventid]);

    const quillFormats = [
        'header', 'font', 'size', 'bold', 'italic', 'underline', 'strike',
        'blockquote', 'list', 'bullet', 'indent', 'color', 'align',
    ];
    const quillModules = useMemo(() => ({
        toolbar: [
            [{ 'header': '1' }, { 'header': '2' }, { 'font': [] }],
            [{ size: ['10px', false, '18px', '32px'] }],
            ['bold', 'italic', 'underline', 'strike', 'blockquote'],
            [{ 'list': 'ordered' }, { 'list': 'bullet' }, { 'indent': '-1' }, { 'indent': '+1' }],
            [{ 'align': [] }],
            ['color'],
            ['clean']
        ],
        clipboard: { matchVisual: false }
    }), []);

    const [images, setImages] = useState({ image: '' });
    const [form, setform] = useState({
        id: '',
        title: '',
        type: '',
        date: "",
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        description: '',
        deadline: '',
        externalLink: '',
        address: '',
        capacity: '',
        groupId: '',
        addedBy: '',
        sendReminderEmail: false,
        reminderDays: [7, 1],
        // Registration List email — per-event override. null = use the group default.
        regListEmailEnabled: null,
        regListEmailMode: null,
        regListEmailDays: null,
        isBreakoutEvent: false,
        zoomMeetingId: '',
        breakoutGroupSize: 4,
    })
    const [reminderDaysText, setReminderDaysText] = useState('7, 1')
    // Registration List per-event override toggle: when off, the three fields are sent as null.
    const [regListOverride, setRegListOverride] = useState(false)
    
    const [isRecurring, setIsRecurring] = useState(false);
    const [seriesId, setSeriesId] = useState(null);
    const [recurrence, setRecurrence] = useState({
        frequency: 'monthly',  // daily, weekly, monthly, yearly
        interval: 1,           // every N days/weeks/months/years
        monthMode: 'date',     // 'date' or 'ordinal' (e.g. "First Friday")
        ordinalWeek: 'First',  // First, Second, Third, Fourth, Last
        ordinalDay: 'Day',     // Day, Monday, Tuesday, etc.
        endType: 'count',      // 'none', 'count', 'date'
        endCount: 4,
        endDate: '',
    });
    const DAYS_OF_WEEK = ['Day', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];
    const ORDINAL_WEEKS = ['First', 'Second', 'Third', 'Fourth', 'Last'];
    const history = useNavigate()
    const [submitted, setSubmitted] = useState(false)
    const [formLoaded, setFormLoaded] = useState(false)
    const initialFormRef = useRef(null)
    const savedRef = useRef(false)
    const formRef = useRef(null)
    const isFormDirty = useCallback(() => {
        if (!formLoaded || !initialFormRef.current || savedRef.current) return false
        const initial = initialFormRef.current
        const stripHtml = (s) => (s || '').replace(/<[^>]*>/g, '').replace(/&nbsp;/g, ' ').trim()
        return Object.keys(initial).some(key => {
            if (key === 'description') return stripHtml(form[key]) !== stripHtml(initial[key])
            return (form[key] || '') !== (initial[key] || '')
        })
    }, [form, formLoaded])

    const isDirtyRef = useRef(false)
    useEffect(() => { isDirtyRef.current = isFormDirty() }, [isFormDirty])

    useEffect(() => {
        const handleBeforeUnload = (e) => {
            if (isDirtyRef.current) {
                e.preventDefault()
                e.returnValue = ''
            }
        }
        window.addEventListener('beforeunload', handleBeforeUnload)
        return () => window.removeEventListener('beforeunload', handleBeforeUnload)
    }, [])

    useEffect(() => {
        const handleClick = (e) => {
            if (!isDirtyRef.current) return
            const link = e.target.closest('a[href]')
            if (!link) return
            const href = link.getAttribute('href')
            if (!href || href.startsWith('http') || href.startsWith('mailto:') || href.startsWith('#')) return
            e.preventDefault()
            e.stopPropagation()
            const canSave = formRef.current && formRef.current.checkValidity()
            Swal.fire({
                title: 'Unsaved Changes',
                text: canSave ? 'You have unsaved changes. Would you like to save before leaving?' : 'You have unsaved changes. Discard them?',
                icon: 'warning',
                showCancelButton: true,
                confirmButtonColor: canSave ? '#EF7A2B' : '#6c757d',
                cancelButtonColor: '#d33',
                confirmButtonText: canSave ? 'Save' : 'Stay on page',
                cancelButtonText: 'Discard',
            }).then((result) => {
                if (result.isConfirmed && canSave) {
                    formRef.current.requestSubmit()
                    return
                }
                if (result.dismiss === Swal.DismissReason.cancel) {
                    savedRef.current = true
                    window.location.href = href
                }
            })
        }
        document.addEventListener('click', handleClick, true)
        return () => document.removeEventListener('click', handleClick, true)
    }, [])

    useEffect(() => {
        window.history.pushState(null, '', window.location.href)
        const handlePopState = () => {
            if (!isDirtyRef.current) {
                window.history.back()
                return
            }
            window.history.pushState(null, '', window.location.href)
            const canSave = formRef.current && formRef.current.checkValidity()
            Swal.fire({
                title: 'Unsaved Changes',
                text: canSave ? 'You have unsaved changes. Would you like to save before leaving?' : 'You have unsaved changes. Discard them?',
                icon: 'warning',
                showCancelButton: true,
                confirmButtonColor: canSave ? '#EF7A2B' : '#6c757d',
                cancelButtonColor: '#d33',
                confirmButtonText: canSave ? 'Save' : 'Stay on page',
                cancelButtonText: 'Discard',
            }).then((result) => {
                if (result.isConfirmed && canSave) {
                    formRef.current.requestSubmit()
                    return
                }
                if (result.dismiss === Swal.DismissReason.cancel) {
                    savedRef.current = true
                    window.history.go(-2)
                }
            })
        }
        window.addEventListener('popstate', handlePopState)
        return () => window.removeEventListener('popstate', handlePopState)
    }, [])
    const user = useSelector((state) => state.user);
    const connectMeetLabel = user?.connectMeetLabel || 'ConnectMeet';

    // cw: Global email settings from Settings page (used for both RSVP and ConnectMeet previews)
    const [globalRsvpSettings, setGlobalRsvpSettings] = useState({});
    const [sendingTestEmail, setSendingTestEmail] = useState(false);
    const [logoSettings, setLogoSettings] = useState({ x: 0, y: 0, size: 150, padding: 0 });
    const [cmLogoSettings, setCmLogoSettings] = useState({ x: 0, y: 0, size: 150, padding: 0 });
    const [cmHeaderText, setCmHeaderText] = useState('');
    const [cmFooterText, setCmFooterText] = useState('');
    const [rsvpHeaderText, setRsvpHeaderText] = useState('');
    const [rsvpFooterText, setRsvpFooterText] = useState('');
    const [emailTab, setEmailTab] = useState('rsvp');
    const [rsvpSubjectLine, setRsvpSubjectLine] = useState('');
    const connectMeetLabelPlural = connectMeetLabel + 's';

    useEffect(() => {
        const groupId = user?.groupId?._id;
        if (groupId) {
            ApiClient.get(`api/event-group/setting?groupId=${groupId}`).then(res => {
                if (res.success && res.data?.length > 0) {
                    setGlobalRsvpSettings(res.data[0]);
                    const d = res.data[0];
                    setLogoSettings({
                        x: d?.rsvpLogoX || 0,
                        y: d?.rsvpLogoY || 0,
                        size: d?.rsvpLogoSize || 150,
                        padding: d?.rsvpLogoPadding || 0,
                    });
                    setCmLogoSettings({
                        x: d?.connectmeetLogoX || 0,
                        y: d?.connectmeetLogoY || 0,
                        size: d?.connectmeetLogoSize || 150,
                        padding: d?.connectmeetLogoPadding || 0,
                    });
                    setCmHeaderText(d?.emailHeaderText || '');
                    setCmFooterText(d?.emailFooterText || '');
                    setRsvpHeaderText(d?.rsvpEmailHeaderText || '');
                    setRsvpFooterText(d?.rsvpEmailFooterText || '');
                    // For a NEW event, prefill the reminder schedule from the group default (skip if copying from previous event)
                    if (!eventid && !copiedPrevious && Array.isArray(d?.reminderDays) && d.reminderDays.length) {
                        setReminderDaysText(d.reminderDays.join(', '));
                        setform(f => ({ ...f, reminderDays: d.reminderDays }));
                    }
                }
            });
        }
    }, [user?.groupId?._id]);

    const logoSaveTimer = useRef(null);
    const updateLogoSetting = (key, value) => {
        setLogoSettings(prev => {
            const updated = { ...prev, [key]: value };
            if (logoSaveTimer.current) clearTimeout(logoSaveTimer.current);
            logoSaveTimer.current = setTimeout(() => {
                const logoFields = {
                    rsvpLogoX: updated.x,
                    rsvpLogoY: updated.y,
                    rsvpLogoSize: updated.size,
                    rsvpLogoPadding: updated.padding,
                };
                if (globalRsvpSettings?._id) {
                    ApiClient.put('api/event-group/setting/update', { id: globalRsvpSettings._id, ...logoFields });
                } else if (user?.groupId?._id) {
                    ApiClient.post('api/event-group/setting', { groupId: user.groupId._id, ...logoFields }).then(res => {
                        if (res.success && res.data) setGlobalRsvpSettings(res.data);
                    });
                }
            }, 500);
            return updated;
        });
    };

    const cmLogoSaveTimer = useRef(null);
    const updateCmLogoSetting = (key, value) => {
        setCmLogoSettings(prev => {
            const updated = { ...prev, [key]: value };
            if (cmLogoSaveTimer.current) clearTimeout(cmLogoSaveTimer.current);
            cmLogoSaveTimer.current = setTimeout(() => {
                const cmLogoFields = {
                    connectmeetLogoX: updated.x,
                    connectmeetLogoY: updated.y,
                    connectmeetLogoSize: updated.size,
                    connectmeetLogoPadding: updated.padding,
                };
                if (globalRsvpSettings?._id) {
                    ApiClient.put('api/event-group/setting/update', { id: globalRsvpSettings._id, ...cmLogoFields });
                } else if (user?.groupId?._id) {
                    ApiClient.post('api/event-group/setting', { groupId: user.groupId._id, ...cmLogoFields }).then(res => {
                        if (res.success && res.data) setGlobalRsvpSettings(res.data);
                    });
                }
            }, 500);
            return updated;
        });
    };

    const emailTextSaveTimer = useRef(null);
    const saveEmailText = (fields) => {
        if (emailTextSaveTimer.current) clearTimeout(emailTextSaveTimer.current);
        emailTextSaveTimer.current = setTimeout(() => {
            if (globalRsvpSettings?._id) {
                ApiClient.put('api/event-group/setting/update', { id: globalRsvpSettings._id, ...fields });
            } else if (user?.groupId?._id) {
                ApiClient.post('api/event-group/setting', { groupId: user.groupId._id, ...fields }).then(res => {
                    if (res.success && res.data) {
                        setGlobalRsvpSettings(res.data);
                    }
                });
            }
        }, 800);
    };

    const naiveDateToUtc = (naiveDate, tz) => {
        if (!naiveDate || !tz) return naiveDate;
        const [datePart, timePart] = naiveDate.split('T');
        if (!timePart) return naiveDate;
        const [year, month, day] = datePart.split('-').map(Number);
        const [hour, minute] = timePart.split(':').map(Number);
        const formatter = new Intl.DateTimeFormat('en-US', {
            timeZone: tz, year: 'numeric', month: '2-digit', day: '2-digit',
            hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false
        });
        const testDate = new Date(Date.UTC(year, month - 1, day, hour, minute, 0));
        const parts = formatter.formatToParts(testDate);
        const getPart = (type) => parseInt(parts.find(p => p.type === type)?.value || '0');
        const utcMs = Date.UTC(year, month - 1, day, hour, minute, 0);
        const tzMs = Date.UTC(getPart('year'), getPart('month') - 1, getPart('day'), getPart('hour'), getPart('minute'), 0);
        return new Date(utcMs - (tzMs - utcMs)).toISOString();
    };

    const handleSendTestEmail = () => {
        setSendingTestEmail(true);
        const payload = id
            ? { eventId: id, userId: user._id, groupId: user?.groupId?._id, rsvpSubjectLine: rsvpSubjectLine || '' }
            : {
                userId: user._id,
                groupId: user?.groupId?._id,
                title: form.title,
                date: naiveDateToUtc(form.date, form.timezone),
                address: form.address,
                timezone: form.timezone,
                description: form.description,
                rsvpSubjectLine: rsvpSubjectLine || '',
            };
        ApiClient.post('api/event/test-rsvp-email', payload).then(res => {
            if (res.success) {
                toast.success(res.message || 'Test email sent!');
            } else {
                toast.error(res.message || 'Failed to send test email');
            }
            setSendingTestEmail(false);
        }).catch(() => {
            toast.error('Failed to send test email');
            setSendingTestEmail(false);
        });
    };

    // cw: Strip HTML from description for preview, preserving line breaks
    const stripHtml = (html) => {
        if (!html) return '';
        let text = html;
        text = text.replace(/<\/p>\s*<p[^>]*>/gi, '\n');
        text = text.replace(/<br\s*\/?>/gi, '\n');
        text = text.replace(/<\/?(p|div)[^>]*>/gi, '\n');
        const tmp = document.createElement('DIV');
        tmp.innerHTML = text;
        let result = tmp.textContent || tmp.innerText || '';
        result = result.replace(/\n{3,}/g, '\n\n').trim();
        return result;
    };
    const formValidation = [
        // { key: 'status', required: true },
        { key: 'type', required: true, message: 'Type is required' },
        { key: 'timezone', required: true },
        { key: 'date', required: true, message: 'Event Date is required' },
        { key: 'deadline', required: true, message: 'Deadline is required' },
        // { key:'description' , required:true ,message:'Description is required'},
    ]
    const timezones = timezoneModel.list
   
    // cw: event page stores user profile time zone
    const handleSubmit = async (e) => {
        e.preventDefault()
        setSubmitted(true)
        let invalid = methodModel.getFormError(formValidation, form)
    
        const isPastDate = !form.id && getDateErrr(form.date, datepipeModel.convwertTimezoneToDate(current.toISOString(), form.timezone))
        const deadlineAfterEvent = getDateErrr(form.date, form.deadline)
        if (invalid || deadlineAfterEvent || isPastDate) {
            if (invalid) toast?.error('Please fill in all required fields');
            else if (deadlineAfterEvent) toast?.error('Deadline must be before the event date');
            else if (isPastDate) toast?.error('Event date cannot be in the past');
            setTimeout(() => {
                const firstError = document.querySelector('.text-danger');
                if (firstError) {
                    const target = firstError.closest('.formWrapper') || firstError.parentElement || firstError;
                    target.scrollIntoView({ behavior: 'smooth', block: 'start' });
                }
            }, 100);
            return;
        }
        let method = 'post'
        let url = shared.addApi
    
        let value = {
            ...form,
            ...images,
            rsvpSubjectLine: rsvpSubjectLine || '',
        }

        // Registration List per-event override: when the override is off, send null for all three
        // so the cron falls back to the group default. When on, coerce days to a number.
        if (!regListOverride) {
            value.regListEmailEnabled = null
            value.regListEmailMode = null
            value.regListEmailDays = null
        } else {
            value.regListEmailEnabled = !!form.regListEmailEnabled
            value.regListEmailMode = form.regListEmailMode || 'before_event'
            value.regListEmailDays = (form.regListEmailDays === '' || form.regListEmailDays === null || form.regListEmailDays === undefined)
                ? 3
                : Number(form.regListEmailDays)
        }

        // Convert datetime-local to UTC based on SELECTED timezone (not browser timezone)
        if (value.date && value.timezone) {
            // value.date is like "2025-12-14T23:15" - user means this time in the SELECTED timezone
            // We need to convert this to UTC
            const [datePart, timePart] = value.date.split('T');
            const [year, month, day] = datePart.split('-').map(Number);
            const [hour, minute] = timePart.split(':').map(Number);
            
            // Create a formatter that will tell us the UTC offset for the selected timezone
            const formatter = new Intl.DateTimeFormat('en-US', {
                timeZone: value.timezone,
                year: 'numeric', month: '2-digit', day: '2-digit',
                hour: '2-digit', minute: '2-digit', second: '2-digit',
                hour12: false
            });
            
            // Create a date assuming UTC, then find what local time that would be in target timezone
            // Then calculate the offset
            const testDate = new Date(Date.UTC(year, month - 1, day, hour, minute, 0));
            const parts = formatter.formatToParts(testDate);
            const getPart = (type) => parseInt(parts.find(p => p.type === type)?.value || '0');
            
            const tzYear = getPart('year');
            const tzMonth = getPart('month');
            const tzDay = getPart('day');
            const tzHour = getPart('hour');
            const tzMinute = getPart('minute');
            
            // Calculate offset in milliseconds
            const utcMs = Date.UTC(year, month - 1, day, hour, minute, 0);
            const tzMs = Date.UTC(tzYear, tzMonth - 1, tzDay, tzHour, tzMinute, 0);
            const offsetMs = tzMs - utcMs;
            
            // The user entered time in selected timezone, so subtract offset to get UTC
            value.date = new Date(utcMs - offsetMs).toISOString();
        }
    
        if (value.deadline && value.timezone) {
            const [datePart, timePart] = value.deadline.split('T');
            const [year, month, day] = datePart.split('-').map(Number);
            const [hour, minute] = timePart.split(':').map(Number);
            
            const formatter = new Intl.DateTimeFormat('en-US', {
                timeZone: value.timezone,
                year: 'numeric', month: '2-digit', day: '2-digit',
                hour: '2-digit', minute: '2-digit', second: '2-digit',
                hour12: false
            });
            
            const testDate = new Date(Date.UTC(year, month - 1, day, hour, minute, 0));
            const parts = formatter.formatToParts(testDate);
            const getPart = (type) => parseInt(parts.find(p => p.type === type)?.value || '0');
            
            const tzYear = getPart('year');
            const tzMonth = getPart('month');
            const tzDay = getPart('day');
            const tzHour = getPart('hour');
            const tzMinute = getPart('minute');
            
            const utcMs = Date.UTC(year, month - 1, day, hour, minute, 0);
            const tzMs = Date.UTC(tzYear, tzMonth - 1, tzDay, tzHour, tzMinute, 0);
            const offsetMs = tzMs - utcMs;
            
            value.deadline = new Date(utcMs - offsetMs).toISOString();
        }
    
        if (value.id) {
            method = 'put'
            url = shared.editApi
        } else {
            value.addedBy = user._id
            value.groupId = user?.groupId?._id
            delete value.id
        }

        if (value.id && seriesId && !isRecurring) {
            const swalResult = await Swal.fire({
                title: 'Remove Recurring Events?',
                text: 'This will delete all other events in this series and keep only the earliest one.',
                icon: 'warning',
                showCancelButton: true,
                confirmButtonColor: '#EF7A2B',
                cancelButtonColor: '#6c757d',
                confirmButtonText: 'Yes, remove series',
            });
            if (!swalResult.isConfirmed) return;
            loader(true);
            ApiClient.put('api/event/unrecur-series', { seriesId }).then((res) => {
                savedRef.current = true;
                if (res.success) {
                    toast?.success(res.message);
                }
                loader(false);
                window.location.href = `/${shared.url}`;
            });
            return;
        }

        loader(true)

        if (isRecurring) {
            try {
            if (seriesId && value.id) {
                await ApiClient.delete('api/event/delete-series', { seriesId, keepId: value.id });
            }
            const baseDateMs = new Date(value.date).getTime();
            const deadlineOffsetMs = value.deadline ? new Date(value.deadline).getTime() - baseDateMs : 0;

            const getNthWeekdayOfMonth = (year, month, ordWeek, ordDay) => {
                const dayNames = ['Day', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];
                const weekNames = ['First', 'Second', 'Third', 'Fourth', 'Last'];
                if (ordDay === 'Day') {
                    if (ordWeek === 'Last') return new Date(year, month + 1, 0);
                    return new Date(year, month, weekNames.indexOf(ordWeek) + 1);
                }
                const targetDow = dayNames.indexOf(ordDay);
                const jsDow = targetDow === 7 ? 0 : targetDow;
                if (ordWeek === 'Last') {
                    let d = new Date(year, month + 1, 0).getDate();
                    while (new Date(year, month, d).getDay() !== jsDow) d--;
                    return new Date(year, month, d);
                }
                const n = weekNames.indexOf(ordWeek) + 1;
                let count = 0;
                for (let d = 1; d <= 31; d++) {
                    const dt = new Date(year, month, d);
                    if (dt.getMonth() !== month) break;
                    if (dt.getDay() === jsDow) { count++; if (count === n) return dt; }
                }
                return getNthWeekdayOfMonth(year, month, 'Last', ordDay);
            };

            const getRecurrenceDate = (baseDate, idx) => {
                if (idx === 0) return new Date(baseDate);
                const d = new Date(baseDate);
                const { frequency, interval, monthMode, ordinalWeek, ordinalDay } = recurrence;
                switch (frequency) {
                    case 'daily':
                        d.setDate(d.getDate() + (idx * interval));
                        break;
                    case 'weekly':
                        d.setDate(d.getDate() + (idx * interval * 7));
                        break;
                    case 'monthly':
                        if (monthMode === 'date') {
                            d.setMonth(d.getMonth() + (idx * interval));
                        } else {
                            d.setMonth(d.getMonth() + (idx * interval));
                            const target = getNthWeekdayOfMonth(d.getFullYear(), d.getMonth(), ordinalWeek, ordinalDay);
                            d.setTime(target.getTime());
                        }
                        break;
                    case 'yearly':
                        d.setFullYear(d.getFullYear() + (idx * interval));
                        break;
                    default: break;
                }
                return d;
            };

            const eventDates = [];
            const maxEvents = 100;
            if (recurrence.endType === 'count') {
                for (let i = 0; i < recurrence.endCount && i < maxEvents; i++) {
                    eventDates.push(getRecurrenceDate(value.date, i));
                }
            } else if (recurrence.endType === 'date' && recurrence.endDate) {
                const endMs = new Date(recurrence.endDate).getTime();
                for (let i = 0; i < maxEvents; i++) {
                    const d = getRecurrenceDate(value.date, i);
                    if (d.getTime() > endMs) break;
                    eventDates.push(d);
                }
            } else {
                const cutoff = new Date(value.date);
                cutoff.setMonth(cutoff.getMonth() + 18);
                const cutoffMs = cutoff.getTime();
                for (let i = 0; i < maxEvents; i++) {
                    const d = getRecurrenceDate(value.date, i);
                    if (d.getTime() >= cutoffMs) break;
                    eventDates.push(d);
                }
            }

            const newSeriesId = `series_${Date.now()}_${Math.random().toString(36).slice(2, 10)}`;
            const isExisting = !!value.id;
            const datesToCreate = isExisting ? eventDates.slice(1) : eventDates;

            const bulkTemplate = { ...value };
            delete bulkTemplate.id;
            bulkTemplate.addedBy = value.addedBy || user._id;
            bulkTemplate.groupId = value.groupId || user?.groupId?._id;

            const bulkEvents = datesToCreate.map((d) => ({
                ...bulkTemplate,
                date: d.toISOString(),
                deadline: value.deadline ? new Date(d.getTime() + deadlineOffsetMs).toISOString() : '',
                title: value.title,
                seriesId: newSeriesId,
                recurrenceSettings: recurrence
            }));

            if (isExisting) {
                ApiClient.put(shared.editApi, { ...value, seriesId: newSeriesId, recurrenceSettings: recurrence }).then(async () => {
                    if (bulkEvents.length > 0) {
                        ApiClient.post('api/event/create-bulk', { events: bulkEvents }).then(async (res) => {
                            savedRef.current = true;
                            if (res.success) {
                                toast?.success(`Created ${bulkEvents.length + 1} recurring events`);
                            } else {
                                toast?.error(res.message || 'Failed to create recurring copies');
                            }
                            loader(false);
                            window.location.href = `/${shared.url}`;
                        }).catch(() => { toast?.error('Failed to create recurring copies'); loader(false); });
                    } else {
                        savedRef.current = true;
                        loader(false);
                        window.location.href = `/${shared.url}`;
                    }
                }).catch(() => { toast?.error('Failed to update original event'); loader(false); });
            } else {
                const allEvents = [{ ...bulkTemplate, date: eventDates[0].toISOString(), deadline: value.deadline ? new Date(eventDates[0].getTime() + deadlineOffsetMs).toISOString() : '', title: value.title, seriesId: newSeriesId, recurrenceSettings: recurrence }, ...bulkEvents];
                ApiClient.post('api/event/create-bulk', { events: allEvents }).then(async (res) => {
                    savedRef.current = true;
                    if (res.success) {
                        toast?.success(res.message || `Created ${allEvents.length} recurring events`);
                        if (res.data?.id) {
                            history(`/${shared.url}/detail/${await encryptId(res.data.id)}`);
                        } else {
                            history(`/${shared.url}`);
                        }
                    } else {
                        toast?.error(res.message || 'Failed to create events');
                    }
                    loader(false);
                }).catch(() => { toast?.error('Failed to create recurring events'); loader(false); });
            }
            } catch (err) {
                toast?.error('Error creating recurring events: ' + err.message);
                loader(false);
                return;
            }
        } else {
            ApiClient.allApi(url, value, method).then(async (res) => {
                if (res.success) {
                    savedRef.current = true
                    toast?.success(res?.message)
                    const eventId = res?.data?.id || res?.data?._id || form.id || id
                    if (eventId) {
                        history(`/${shared.url}/detail/${await encryptId(eventId)}`)
                    } else {
                        history(`/${shared.url}`)
                    }
                }
                loader(false)
            })
        }
    }

    const handleEditSeries = () => {
        let value = {
            ...form,
            ...images,
            rsvpSubjectLine: rsvpSubjectLine || '',
            seriesId
        };
        delete value.id;
        delete value.date;
        delete value.deadline;
        loader(true);
        ApiClient.put('api/event/edit-series', value).then(async (res) => {
            if (res.success) {
                savedRef.current = true;
                toast?.success(res?.message);
                const eventId = form.id || id;
                if (eventId) {
                    history(`/${shared.url}/detail/${await encryptId(eventId)}`);
                } else {
                    history(`/${shared.url}`);
                }
            }
            loader(false);
        });
    };

    const handleDeleteSeries = async () => {
        const confirm = await Swal.fire({
            title: 'Delete Entire Series?',
            text: 'This will permanently delete ALL events in this series, including the original.',
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#d33',
            cancelButtonColor: '#6c757d',
            confirmButtonText: 'Yes, delete all',
        });
        if (!confirm.isConfirmed) return;
        loader(true);
        ApiClient.delete('api/event/delete-series', { seriesId }).then((res) => {
            savedRef.current = true;
            if (res.success) {
                toast?.success(res.message);
                history(`/${shared.url}`);
            } else {
                toast?.error(res.message || 'Failed to delete series');
            }
            loader(false);
        });
    };

    useEffect(() => {
        if (id) {
            loader(true)
            ApiClient.get(shared.detailApi, { id }).then(res => {
                if (res.success) {
                    let value = res.data
                    let payload = form

                    Object.keys(payload).map(itm => {
                        payload[itm] = value[itm]
                    })
                    if (payload.groupId && typeof payload.groupId === 'object') {
                        payload.groupId = payload.groupId._id
                    }
                    if (payload.addedBy && typeof payload.addedBy === 'object') {
                        payload.addedBy = payload.addedBy._id
                    }

                    // Convert UTC dates to the event's timezone for display in the form
                    const tz = payload.timezone || user?.timezone || 'America/Los_Angeles'
                    if (payload.date) {
                        payload.date = datepipeModel.convwertTimezoneToDate(payload.date, tz)
                    }
                    if (payload.deadline) {
                        payload.deadline = datepipeModel.convwertTimezoneToDate(payload.deadline, tz)
                    }

                    payload.id = id
                    setform({
                        ...payload
                    })
                    if (Array.isArray(value.reminderDays) && value.reminderDays.length) {
                        setReminderDaysText(value.reminderDays.join(', '))
                    }
                    setRegListOverride(value.regListEmailEnabled !== null && value.regListEmailEnabled !== undefined)
                    initialFormRef.current = { ...payload }
                    setFormLoaded(true)
                    if (value.rsvpSubjectLine) setRsvpSubjectLine(value.rsvpSubjectLine)
                    if (value.seriesId) {
                        setSeriesId(value.seriesId)
                        setIsRecurring(true)
                        if (value.recurrenceSettings) setRecurrence(value.recurrenceSettings)
                    }

                    let img = images
                    Object.keys(img).map(itm => {
                        img[itm] = value[itm]
                    })
                    setImages({ ...img })


                }
                loader(false)
            })
        } else {
            initialFormRef.current = { ...form }
            setFormLoaded(true)
        }

    }, [id])

    const imageResult = (e, key) => {
        images[key] = e.value
        setImages(images)
    }

    const [copiedPrevious, setCopiedPrevious] = useState(false);
    const [recentEvents, setRecentEvents] = useState([]);
    const [showCopyDropdown, setShowCopyDropdown] = useState(false);
    const copyDropdownRef = useRef(null);

    useEffect(() => {
        const handleClickOutside = (e) => {
            if (copyDropdownRef.current && !copyDropdownRef.current.contains(e.target)) {
                setShowCopyDropdown(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const fetchRecentEvents = () => {
        if (recentEvents.length > 0) { setShowCopyDropdown(true); return; }
        const groupId = user?.lastUsedGroupId?._id || user?.lastUsedGroupId || user?.groupId?._id || user?.groupId || user?.groups?.[0]?.groupId?._id || user?.groups?.[0]?.groupId;
        if (!groupId) { toast.error('No group selected'); return; }
        ApiClient.get('api/event/all', { groupId, sortBy: 'createdAt desc', count: 5, page: 1, _t: Date.now() }).then(res => {
            const events = res?.data || [];
            if (!events.length) { toast.error('No previous events found for this group'); return; }
            setRecentEvents(events);
            setShowCopyDropdown(true);
        }).catch(() => { toast.error('Could not load previous events'); });
    };

    const clearTemplate = () => {
        setform(f => ({
            ...f,
            title: '',
            type: '',
            description: '',
            address: '',
            capacity: '',
            externalLink: '',
            sendReminderEmail: false,
            reminderDays: [7, 1],
            date: '',
            deadline: '',
        }));
        setReminderDaysText('7, 1');
        setCopiedPrevious(false);
    };

    const copyFromEvent = (prev) => {
        setform(f => ({
            ...f,
            title: prev.title || '',
            type: prev.type || '',
            description: prev.description || '',
            address: prev.address || '',
            capacity: prev.capacity || '',
            externalLink: prev.externalLink || '',
            sendReminderEmail: prev.sendReminderEmail || false,
            reminderDays: prev.reminderDays || [7, 1],
            isBreakoutEvent: prev.isBreakoutEvent || false,
            zoomMeetingId: prev.zoomMeetingId || '',
            breakoutGroupSize: prev.breakoutGroupSize || 4,
            date: '',
            deadline: '',
        }));
        if (prev.reminderDays?.length) setReminderDaysText(prev.reminderDays.join(', '));
        setCopiedPrevious(true);
        setShowCopyDropdown(false);
        toast.success('Template copied from: ' + prev.title);
    };

    const getError = (key) => {
        return submitted ? methodModel.getError(key, form, formValidation)?.message : ''
    }

    const getDateErrr = (start, end = new Date()) => {
        let value = false
        if (start && end) {
            if (new Date(datepipeModel.datetodatepicker(start)).getTime() < new Date(datepipeModel.datetodatepicker(end)).getTime()) {
                value = true
            }
        }

        return value
    }

    const current = new Date()

    return <>
        <Layout>
            <form ref={formRef} onSubmit={handleSubmit}>
                <div className="pprofile1">


                    <div className='flex items-center justify-between mb-8'>
                        <div className='flex items-center'>
                        <Tooltip placement="top" title="Back">
                            <button type="button" onClick={() => {
                                if (isFormDirty()) {
                                    const canSave = formRef.current && formRef.current.checkValidity()
                                    Swal.fire({
                                        title: 'Unsaved Changes',
                                        text: canSave ? 'You have unsaved changes. Would you like to save before leaving?' : 'You have unsaved changes. Discard them?',
                                        icon: 'warning',
                                        showCancelButton: true,
                                        confirmButtonColor: canSave ? '#EF7A2B' : '#6c757d',
                                        cancelButtonColor: '#d33',
                                        confirmButtonText: canSave ? 'Save' : 'Stay on page',
                                        cancelButtonText: 'Discard',
                                    }).then((result) => {
                                        if (result.isConfirmed && canSave) { formRef.current.requestSubmit(); return; }
                                        if (result.dismiss === Swal.DismissReason.cancel) { savedRef.current = true; window.location.href = '/event'; }
                                    })
                                } else {
                                    history('/event')
                                }
                            }} className="!px-4  py-2 flex items-center justify-center  rounded-lg shadow-btn hover:bg-[#F3F2F5] border transition-all  mr-3"><i className='fa fa-angle-left text-lg'></i></button>
                        </Tooltip>
                        <div>
                            <h3 className="text-lg lg:text-2xl font-semibold text-[#111827]">
                                {form && form.id ? 'Edit' : 'Add'} {shared.title}
                            </h3>
                            <p class="text-xs lg:text-sm font-normal text-[#75757A]">Here you can see all about your {shared.title}</p>
                        </div>
                        </div>
                        <div className="flex items-center gap-2">
                            {!form.id ? (
                                copiedPrevious ? (
                                    <button type="button" onClick={clearTemplate} className="text-white font-medium rounded-lg text-sm px-4 py-2.5 text-center bg-gray-400 hover:bg-gray-500">Clear Template</button>
                                ) : (
                                    <div className="relative" ref={copyDropdownRef}>
                                        <button type="button" onMouseEnter={fetchRecentEvents} onClick={fetchRecentEvents} className="text-white font-medium rounded-lg text-sm px-4 py-2.5 text-center bg-orange-300 hover:bg-orange-400">Copy Previous Event &#9662;</button>
                                        {showCopyDropdown && recentEvents.length > 0 && (
                                            <div className="absolute right-0 mt-1 w-72 bg-white border border-gray-200 rounded-lg shadow-lg z-50 py-1">
                                                {recentEvents.map((evt) => (
                                                    <button key={evt.id || evt._id} type="button" onClick={() => copyFromEvent(evt)} className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-orange-50 truncate">{evt.title}</button>
                                                ))}
                                            </div>
                                        )}
                                    </div>
                                )
                            ) : null}
                            <button type="submit" className="text-white bg-orange-400 bg-orange-600 focus:ring-4 focus:outline-none focus:ring-blue-300 dark:focus:ring-blue-800 font-medium rounded-lg text-sm px-5 py-2.5 text-center">Save</button>
                        </div>
                    </div>



                    <h3 className="ViewUser mb-3"></h3>
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                        <div className=" mb-3">
                            <FormControl
                                type="text"
                                name="title"
                                label="Title"
                                value={form.title}
                                onChange={e => setform(f => ({ ...f, title: e }))}
                                required
                            />
                        </div>

                        <div className=" mb-3">
                            <FormControl
                                type="select"
                                name="type"
                                label="Type"
                                displayValue="name"
                                placeholder="Select Type"
                                value={form.type}
                                onChange={e => setform(f => ({ ...f, type: e }))}
                                options={[
                                    { id: 'inPerson', name: 'In-Person' },
                                    { id: 'virtual', name: 'Virtual' },
                                ]}
                                required
                                error={getError('type')}
                            />
                        </div>
{/* 
                        <div className=" mb-3">
                            <FormControl
                                type="select"
                                name="status"
                                label="Status"
                                displayValue="name"
                                placeholder="Select Status"
                                value={form.status}
                                onChange={e => setform({ ...form, status: e })}
                                options={statusModel.list}
                                required
                                error={getError('status')}
                            />
                        </div> */}

                        <div className=" mb-3 relative">
                            <div className="">
                                <FormControl
                                    type="select"
                                    name="timezone"
                                    label="Timezone"
                                    displayValue="name"
                                    placeholder="Select Timezone"
                                    value={form.timezone}
                                    onChange={e => setform(f => ({ ...f, timezone: e }))}
                                    options={timezones}
                                    theme="search"
                                    required
                                    isClearable={false}
                                    error={getError('timezone')}
                                />

                                <div className="text-xs mt-1">
                                    Current Time: {datepipeModel.formatDateToTimezone(current.toISOString(), form.timezone)}, {(form.timezone || '').replace(/_/g, ' ')}

                                </div>
                            </div>

                        </div>
                        <div className="mb-3 times">
                            <FormControl
                                type="datetime-local"
                                name="date"
                                label="Event Date"
                                value={form.date}
                                onChange={e => {
                                    setform(f => ({ ...f, date: e }));
                                }}
                                required
                                error={submitted && !form.date ? 'Event Date is required' : !form.id && getDateErrr(form.date, datepipeModel.convwertTimezoneToDate(current.toISOString(), form.timezone)) && submitted ? 'Entered date is less than Current Date' : ''}
                            />
                          {/* {datepipeModel.convwertTimezoneToDate(current.toISOString() , form.timezone)} */}
                        </div>

                        <div className=" mb-3">
                            <FormControl
                                type="datetime-local"
                                name="deadline"
                                label=" Deadline"
                                value={form.deadline}
                                onChange={e => setform(f => ({ ...f, deadline: e }))}
                                error={submitted && !form.deadline ? 'Deadline is required' : (getDateErrr(form.date, form.deadline)) && submitted ? 'Entered date is greater than Event Date' : ''}
                                required
                            />
                        </div>


                        <div className=" mb-3">
                            <label className="text-sm font-medium text-gray-700 mb-1 block">Location <span className="text-red-500">*</span></label>
                            <textarea
                                name="address"
                                value={form.address}
                                onChange={e => {
                                    setform(f => ({ ...f, address: e.target.value }));
                                    e.target.style.height = 'auto';
                                    e.target.style.height = e.target.scrollHeight + 'px';
                                }}
                                ref={el => {
                                    if (el) {
                                        el.style.height = 'auto';
                                        el.style.height = el.scrollHeight + 'px';
                                    }
                                }}
                                required
                                rows={3}
                                className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500 resize-vertical overflow-hidden"
                                style={{ minHeight: '76px' }}
                            />
                        </div>



                        <div className=" mb-3">
                            <FormControl
                                type="text"
                                name="capacity"
                                label="Max Capacity"
                                placeholder=""
                                value={form.capacity || ''}
                                onChange={e => setform(f => ({ ...f, capacity: e ? parseInt(e) || '' : '' }))}
                            />
                        </div>

                        <div className=" mb-3">
                            <FormControl
                                type="text"
                                name="externalLink"
                                label="External Link"
                                value={form.externalLink}
                                onChange={e => setform(f => ({ ...f, externalLink: e }))}
                            />
                        </div>

                        <div className="col-span-full mb-3 px-4">
                            <label className="text-sm font-medium mb-1 block">Reminder email schedule (days before event)</label>
                            <input
                                type="text"
                                value={reminderDaysText}
                                onChange={(e) => {
                                    setReminderDaysText(e.target.value);
                                    const parsed = e.target.value
                                        .split(',')
                                        .map(s => parseInt(s.trim(), 10))
                                        .filter(n => !isNaN(n) && n > 0);
                                    setform(f => ({ ...f, reminderDays: parsed }));
                                }}
                                placeholder="7, 1"
                                className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                            />
                            <p className="text-xs text-gray-500 mt-1">Enter one or more numbers separated by commas — each one sends a separate reminder email that many days before the event. For example, <span className="font-medium">7, 1</span> sends one reminder 7 days before and another 1 day before. (Default: 7, 1)</p>
                        </div>

                        <div className="col-span-full mb-3 px-4">
                            <label className="flex items-center gap-2 cursor-pointer">
                                <input
                                    type="checkbox"
                                    checked={form.sendReminderEmail || false}
                                    onChange={(e) => setform(f => ({ ...f, sendReminderEmail: e.target.checked }))}
                                    className="h-4 w-4"
                                />
                                <span className="text-sm font-medium">Send RSVP reminder email 1 day before event</span>
                            </label>
                        </div>

                        {/* Registration List email — per-event override of the group default. */}
                        <div className="col-span-full mb-3 px-4">
                            <label className="flex items-center gap-2 cursor-pointer">
                                <input
                                    type="checkbox"
                                    checked={regListOverride}
                                    onChange={(e) => {
                                        const on = e.target.checked;
                                        setRegListOverride(on);
                                        if (on) {
                                            setform(f => ({
                                                ...f,
                                                regListEmailEnabled: f.regListEmailEnabled === null || f.regListEmailEnabled === undefined ? true : f.regListEmailEnabled,
                                                regListEmailMode: f.regListEmailMode || 'before_event',
                                                regListEmailDays: (f.regListEmailDays === null || f.regListEmailDays === undefined || f.regListEmailDays === '') ? 3 : f.regListEmailDays,
                                            }));
                                        } else {
                                            setform(f => ({ ...f, regListEmailEnabled: null, regListEmailMode: null, regListEmailDays: null }));
                                        }
                                    }}
                                    className="h-4 w-4"
                                />
                                <span className="text-sm font-medium">Send out list of registered attendees (override group default)</span>
                            </label>
                            <p className="text-xs text-gray-500 mt-1">Leave unchecked to use your group's default setting. Check this to customize for this event only.</p>

                            {regListOverride && (
                                <div className="mt-2 pl-6">
                                    <label className="flex items-center gap-2 cursor-pointer mb-2">
                                        <input
                                            type="checkbox"
                                            checked={!!form.regListEmailEnabled}
                                            onChange={(e) => setform(f => ({ ...f, regListEmailEnabled: e.target.checked }))}
                                            className="h-4 w-4"
                                        />
                                        <span className="text-sm font-medium">Enabled for this event</span>
                                    </label>
                                    {form.regListEmailEnabled && (
                                        <div className="flex flex-wrap items-end gap-4">
                                            <div>
                                                <label className="block text-xs text-gray-600 mb-1">When to send</label>
                                                <select
                                                    value={form.regListEmailMode || 'before_event'}
                                                    onChange={(e) => setform(f => ({ ...f, regListEmailMode: e.target.value }))}
                                                    className="border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-orange-500"
                                                >
                                                    <option value="before_event">Before event</option>
                                                    <option value="after_deadline">After RSVP deadline</option>
                                                </select>
                                            </div>
                                            <div>
                                                <label className="block text-xs text-gray-600 mb-1">Number of days</label>
                                                <input
                                                    type="number"
                                                    min="0"
                                                    value={form.regListEmailDays === null || form.regListEmailDays === undefined ? '' : form.regListEmailDays}
                                                    onChange={(e) => setform(f => ({ ...f, regListEmailDays: e.target.value === '' ? '' : Math.max(0, parseInt(e.target.value, 10) || 0) }))}
                                                    className="w-32 border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-orange-500"
                                                />
                                            </div>
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>

                        {/* cw: "This is a Zoom Breakout Event" removed — any event can now generate Zoom
                            breakouts from its detail page, where the meeting ID + group size are entered. */}

                        {/* <div className="col-span-12 md:col-span-6">
                                <label className='lablefontcls'>Image</label><br></br>
                                <ImageUpload model="users" result={e => imageResult(e, 'image')} value={images.image || form.image} />
                            </div> */}
                    </div>

                    {/* cw: recurring events section - Outlook-style */}
                    <div className="col-span-full mb-4">
                        <div className="p-4 border border-gray-200 rounded-lg">
                            <label className="flex items-center gap-2 cursor-pointer font-medium">
                                <input
                                    type="checkbox"
                                    checked={isRecurring}
                                    onChange={(e) => setIsRecurring(e.target.checked)}
                                    className="h-4 w-4"
                                />
                                Create Recurring Events
                            </label>

                            {isRecurring && (
                                <div className="mt-4 space-y-4">
                                    {/* Repeat frequency */}
                                    <div className="flex items-center gap-4">
                                        <label className="text-sm font-medium w-20 shrink-0">Repeat</label>
                                        <select
                                            className="px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-orange-500"
                                            value={recurrence.frequency}
                                            onChange={e => setRecurrence({ ...recurrence, frequency: e.target.value })}
                                        >
                                            <option value="daily">Daily</option>
                                            <option value="weekly">Weekly</option>
                                            <option value="monthly">Monthly</option>
                                            <option value="yearly">Yearly</option>
                                        </select>
                                    </div>

                                    {/* Every N interval */}
                                    <div className="flex items-center gap-4">
                                        <label className="text-sm font-medium w-20 shrink-0">Every</label>
                                        <input
                                            type="text"
                                            inputMode="numeric"
                                            className="w-20 px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-orange-500"
                                            value={recurrence.interval}
                                            onKeyPress={e => { if (!/[0-9]/.test(e.key)) e.preventDefault(); }}
                                            onChange={e => { const v = e.target.value.replace(/\D/g, ''); setRecurrence({ ...recurrence, interval: v === '' ? '' : Math.max(1, parseInt(v)) }); }}
                                            onBlur={() => { if (!recurrence.interval) setRecurrence({ ...recurrence, interval: 1 }); }}
                                        />
                                        <span className="text-sm text-gray-600">
                                            {recurrence.frequency === 'daily' ? 'day(s)' : recurrence.frequency === 'weekly' ? 'week(s)' : recurrence.frequency === 'monthly' ? 'month(s)' : 'year(s)'}
                                        </span>
                                    </div>

                                    {/* Monthly: on specific date vs ordinal day */}
                                    {recurrence.frequency === 'monthly' && (
                                        <div className="flex flex-col gap-3">
                                            <label className="text-sm font-medium w-20 shrink-0">On</label>
                                            <div className="flex items-center gap-3">
                                                <input
                                                    type="radio"
                                                    name="monthMode"
                                                    checked={recurrence.monthMode === 'date'}
                                                    onChange={() => setRecurrence({ ...recurrence, monthMode: 'date' })}
                                                    className="h-4 w-4 accent-blue-600"
                                                />
                                                <span className="text-sm text-gray-700">Day of the month (from event date)</span>
                                            </div>
                                            <div className="flex items-center gap-3">
                                                <input
                                                    type="radio"
                                                    name="monthMode"
                                                    checked={recurrence.monthMode === 'ordinal'}
                                                    onChange={() => setRecurrence({ ...recurrence, monthMode: 'ordinal' })}
                                                    className="h-4 w-4 accent-blue-600"
                                                />
                                                <select
                                                    className="px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-orange-500"
                                                    value={recurrence.ordinalWeek}
                                                    onChange={e => setRecurrence({ ...recurrence, ordinalWeek: e.target.value, monthMode: 'ordinal' })}
                                                >
                                                    {ORDINAL_WEEKS.map(w => <option key={w} value={w}>{w}</option>)}
                                                </select>
                                                <select
                                                    className="px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-orange-500"
                                                    value={recurrence.ordinalDay}
                                                    onChange={e => setRecurrence({ ...recurrence, ordinalDay: e.target.value, monthMode: 'ordinal' })}
                                                >
                                                    {DAYS_OF_WEEK.map(d => <option key={d} value={d}>{d}</option>)}
                                                </select>
                                            </div>
                                        </div>
                                    )}

                                    {/* End condition */}
                                    <div className="flex flex-col gap-3">
                                        <label className="text-sm font-medium w-20 shrink-0">End</label>
                                        <div className="flex items-center gap-3">
                                            <input
                                                type="radio"
                                                name="endType"
                                                checked={recurrence.endType === 'count'}
                                                onChange={() => setRecurrence({ ...recurrence, endType: 'count' })}
                                                className="h-4 w-4 accent-blue-600"
                                            />
                                            <span className="text-sm text-gray-700">After</span>
                                            <input
                                                type="text"
                                                inputMode="numeric"
                                                className="w-20 px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-orange-500"
                                                value={recurrence.endCount}
                                                onKeyPress={e => { if (!/[0-9]/.test(e.key)) e.preventDefault(); }}
                                                onChange={e => { const v = e.target.value.replace(/\D/g, ''); setRecurrence({ ...recurrence, endCount: v === '' ? '' : Math.max(1, parseInt(v)), endType: 'count' }); }}
                                                onBlur={() => { if (!recurrence.endCount) setRecurrence({ ...recurrence, endCount: 1 }); }}
                                            />
                                            <span className="text-sm text-gray-700">events</span>
                                        </div>
                                        <div className="flex items-center gap-3">
                                            <input
                                                type="radio"
                                                name="endType"
                                                checked={recurrence.endType === 'date'}
                                                onChange={() => setRecurrence({ ...recurrence, endType: 'date' })}
                                                className="h-4 w-4 accent-blue-600"
                                            />
                                            <span className="text-sm text-gray-700">By</span>
                                            <input
                                                type="date"
                                                className="px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-orange-500"
                                                value={recurrence.endDate}
                                                onChange={e => setRecurrence({ ...recurrence, endDate: e.target.value, endType: 'date' })}
                                            />
                                        </div>
                                        <div className="flex items-center gap-3">
                                            <input
                                                type="radio"
                                                name="endType"
                                                checked={recurrence.endType === 'none'}
                                                onChange={() => setRecurrence({ ...recurrence, endType: 'none' })}
                                                className="h-4 w-4 accent-blue-600"
                                            />
                                            <span className="text-sm text-gray-700">18 months</span>
                                        </div>
                                    </div>

                                    {/* Summary line */}
                                    {form.date && (
                                        <p className="text-sm text-gray-500 italic border-t pt-3 mt-2">
                                            {(() => {
                                                const startDate = new Date(form.date);
                                                const freq = recurrence.frequency;
                                                const interval = recurrence.interval;
                                                let desc = 'Occurs every ';
                                                if (freq === 'daily') desc += interval === 1 ? 'day' : `${interval} days`;
                                                else if (freq === 'weekly') desc += interval === 1 ? 'week' : `${interval} weeks`;
                                                else if (freq === 'monthly') {
                                                    if (recurrence.monthMode === 'ordinal') {
                                                        desc += `month on the ${recurrence.ordinalWeek.toLowerCase()} ${recurrence.ordinalDay}`;
                                                    } else {
                                                        desc += interval === 1 ? `month on day ${startDate.getDate()}` : `${interval} months on day ${startDate.getDate()}`;
                                                    }
                                                } else if (freq === 'yearly') desc += interval === 1 ? 'year' : `${interval} years`;
                                                desc += ` starting ${startDate.toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}.`;
                                                if (recurrence.endType === 'count') desc += ` Ends after ${recurrence.endCount} events.`;
                                                else if (recurrence.endType === 'date' && recurrence.endDate) desc += ` Ends by ${new Date(recurrence.endDate + 'T00:00:00').toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}.`;
                                                return desc;
                                            })()}
                                        </p>
                                    )}
                                    {form.id && seriesId && (
                                        <div className="border-t pt-3 mt-2">
                                            <button type="button" onClick={handleDeleteSeries} className="text-white bg-red-500 hover:bg-red-600 focus:ring-4 focus:outline-none focus:ring-red-300 font-medium rounded-lg text-sm px-5 py-2.5 text-center">
                                                Delete Entire Series
                                            </button>
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>
                    </div>

                    <div className="col-span-full mb-3 quills">
                        <label>Description</label>
                        <ReactQuill
                            formats={quillFormats}
                            modules={quillModules}
                            theme="snow"
                            value={form.description}
                            onChange={e => setform(f => ({ ...f, description: e }))}
                            className=" "
                        />
                    </div>

                    {/* cw: Email Preview Tabs */}
                    <div className="col-span-full mb-4 mt-2">
                        <div className='flex mb-0 items-end border-b border-gray-200'>
                            {[
                                { key: 'rsvp', label: 'Event RSVP Email' },
                                { key: 'connectmeet', label: `${connectMeetLabel} Email` },
                            ].map((tab) => (
                                <button
                                    key={tab.key}
                                    type="button"
                                    className={`py-2.5 px-5 text-sm font-medium transition-all relative ${
                                        emailTab === tab.key
                                            ? 'bg-orange-500 text-white rounded-t-lg -mb-px z-10'
                                            : 'text-gray-600 hover:text-gray-800 bg-gray-200 rounded-t-lg'
                                    }`}
                                    style={emailTab === tab.key ? {
                                        borderTopLeftRadius: '10px',
                                        borderTopRightRadius: '10px',
                                        paddingBottom: '11px',
                                    } : {
                                        borderTopLeftRadius: '8px',
                                        borderTopRightRadius: '8px',
                                    }}
                                    onClick={() => setEmailTab(tab.key)}
                                >
                                    {tab.label}
                                </button>
                            ))}
                        </div>

                        {/* Event RSVP Email */}
                        {emailTab === 'rsvp' && (
                            <>
                            <div className="p-4 border border-t-0 border-gray-200 rounded-b-lg">
                                <div className="flex items-center justify-between mb-3">
                                    <h4 className="text-lg font-semibold text-gray-800">Event RSVP Email Preview</h4>
                                    <button
                                        type="button"
                                        disabled={sendingTestEmail}
                                        onClick={handleSendTestEmail}
                                        className="text-white bg-orange-500 hover:bg-orange-600 focus:ring-4 focus:outline-none focus:ring-orange-300 font-medium rounded-lg text-sm px-5 py-2.5 text-center disabled:opacity-50"
                                    >
                                        {sendingTestEmail ? 'Sending...' : 'Send Test Email to Me'}
                                    </button>
                                </div>
                                <p className="text-sm text-gray-500 mb-4">This is how the RSVP email will look when you invite attendees via "Invite Member" and "Invite Guest." Editing header/footer text here updates your global Settings.</p>

                                <div className="mb-3 p-4 bg-gray-100 rounded-lg border border-gray-200">
                                    <p className="text-base font-semibold text-gray-600 mb-1">Email Subject Line:</p>
                                    <input
                                        type="text"
                                        value={rsvpSubjectLine || `Please RSVP for ${form.title || '{Event Name}'}${user?.groupId?.name ? ` from ${user.groupId.name}` : ''}`}
                                        onChange={(e) => setRsvpSubjectLine(e.target.value)}
                                        className="w-full text-lg font-bold text-gray-900 bg-white border border-dashed border-gray-300 rounded px-3 py-2 focus:outline-none focus:border-orange-400"
                                        placeholder="Enter email subject line"
                                    />
                                </div>

                                <div style={{ fontFamily: "'Poppins', sans-serif", background: '#f3f3f3', padding: '30px', borderRadius: '8px' }}>
                                    <div style={{ maxWidth: '676px', margin: '0 auto', background: '#fff', position: 'relative' }}>
                                        {globalRsvpSettings.companyLogoUrl && (
                                            <DraggableLogoPreview
                                                logoUrl={`${environment.sasurl}/${environment.container}/${encodeURIComponent(globalRsvpSettings.companyLogoUrl)}`}
                                                settings={logoSettings}
                                                onUpdateSetting={updateLogoSetting}
                                            />
                                        )}
                                        <div style={{ padding: '20px 60px 10px' }}>
                                            <div style={{ borderBottom: '1px solid #d77c2f' }}></div>
                                        </div>
                                        <div style={{ padding: '10px 60px' }}>
                                            <p style={{ fontSize: '20px', fontWeight: '500', padding: '5px 0px 15px', color: '#000', lineHeight: '18px' }}>
                                                Hi <span style={{ fontWeight: '500', color: '#EF7A2B' }}>{'{'}<span>User1 Name</span>{'}'}</span>,
                                            </p>
                                        </div>
                                        <div style={{ padding: '7px 60px 10px' }}>
                                            {rsvpHeaderText ? <div className="ql-editor" style={{ padding: '6px', minHeight: 'auto', color: '#393C3D', lineHeight: '22px', fontSize: '14px' }} dangerouslySetInnerHTML={{ __html: rsvpHeaderText }} /> : <p style={{ color: '#aaa', fontSize: '14px', margin: 0 }}>Header text (appears above the event details)</p>}
                                        </div>
                                        <div style={{ padding: '7px 60px 5px', color: '#000', fontSize: '13px', textTransform: 'capitalize' }}>
                                            <span style={{ marginRight: '5px', color: '#EF7A2B' }}>📅</span> {form.date ? datepipeModel.formatNaiveDateWithTz(form.date, form.timezone) : '{Event Date}'}
                                        </div>
                                        <div style={{ padding: '7px 60px 20px', color: '#000', fontSize: '13px', textTransform: 'capitalize' }}>
                                            <span style={{ display: 'inline-block', verticalAlign: 'top' }}>
                                                <span style={{ marginRight: '5px', color: '#EF7A2B' }}>📍</span>
                                            </span>
                                            <span style={{ display: 'inline-block', verticalAlign: 'top' }}>
                                                {form.address
                                                    ? form.address.split('\n').map((line, i) => (
                                                        <div key={i}>{line}</div>
                                                    ))
                                                    : '{Location}'}
                                            </span>
                                        </div>
                                        <div style={{ padding: '7px 60px 15px', color: '#000', fontSize: '14px', fontWeight: '500' }}>Here are some additional details about this event.</div>
                                        <div style={{ padding: '0px 60px' }}>
                                            <div style={{ margin: '0px', border: '1px solid rgba(215, 124, 47, 0.58)', padding: '6px 9px', borderRadius: '4px' }}>
                                                {form.description ? <div className="ql-editor" style={{ padding: 0, minHeight: 'auto' }} dangerouslySetInnerHTML={{ __html: form.description }} /> : <span>{'{Event Description}'}</span>}
                                            </div>
                                        </div>
                                        <div style={{ padding: '15px 60px', display: 'flex', gap: '10px' }}>
                                            <span style={{ background: '#EF7A2B', color: '#fff', padding: '6px 12px', width: '80px', fontSize: '14px', textAlign: 'center', border: '1px solid #d77c2f', borderRadius: '3px', display: 'inline-block' }}>Yes</span>
                                            <span style={{ border: '1px solid #EF7A2B', color: '#EF7A2B', padding: '6px 12px', width: '80px', fontSize: '14px', textAlign: 'center', borderRadius: '3px', display: 'inline-block', marginLeft: '10px' }}>No</span>
                                        </div>
                                        <div style={{ padding: '7px 60px 15px' }}>
                                            {rsvpFooterText ? <div className="ql-editor" style={{ padding: '6px', minHeight: 'auto', color: '#393C3D', lineHeight: '22px', fontSize: '14px' }} dangerouslySetInnerHTML={{ __html: rsvpFooterText }} /> : <p style={{ color: '#aaa', fontSize: '14px', margin: 0 }}>Footer text (appears below the Yes/No buttons)</p>}
                                        </div>
                                        <div style={{ height: '30px' }}></div>
                                    </div>
                                </div>
                            </div>
                            {globalRsvpSettings.companyLogoUrl && (
                                <div className='mt-3 p-3 bg-gray-50 rounded-lg'>
                                    <div className='flex items-center gap-4 flex-wrap'>
                                        <div className='flex items-center gap-2 flex-1 min-w-[200px]'>
                                            <label className='text-sm text-gray-600 whitespace-nowrap'>Size:</label>
                                            <input type="range" min="40" max="400" value={logoSettings.size} onChange={(e) => updateLogoSetting('size', parseInt(e.target.value))} className='flex-1' />
                                            <span className='text-sm text-gray-500 w-12'>{logoSettings.size}px</span>
                                        </div>
                                        <div className='flex items-center gap-2 flex-1 min-w-[200px]'>
                                            <label className='text-sm text-gray-600 whitespace-nowrap'>Space below:</label>
                                            <input type="range" min="0" max="100" value={logoSettings.padding} onChange={(e) => updateLogoSetting('padding', parseInt(e.target.value))} className='flex-1' />
                                            <span className='text-sm text-gray-500 w-12'>{logoSettings.padding}px</span>
                                        </div>
                                    </div>
                                    <p className='text-xs text-gray-400 mt-1'>Drag the logo in the preview to reposition it</p>
                                </div>
                            )}
                            </>
                        )}

                        {/* ConnectMeet Group Email */}
                        {emailTab === 'connectmeet' && (
                            <>
                            <div className="p-4 border border-t-0 border-gray-200 rounded-b-lg">
                                <div className="flex items-center justify-between mb-3">
                                    <h4 className="text-lg font-semibold text-gray-800">{connectMeetLabel} Group Email Preview</h4>
                                </div>
                                <p className="text-sm text-gray-500 mb-4">This is the email sent to attendees after {connectMeetLabelPlural} are generated. Editing header/footer text here updates your global Settings.</p>

                                <div className="mb-3 p-4 bg-gray-100 rounded-lg border border-gray-200">
                                    <p className="text-base font-semibold text-gray-600 mb-1">Email Subject Line:</p>
                                    <p className="text-lg font-bold text-gray-900">{user?.groupId?.name || '{Group Name}'}, {form.title || '{Event Name}'} {form.date ? new Date(form.date).toLocaleDateString('en-US', { day: 'numeric', month: 'short', year: 'numeric' }) : '{Date}'} - {connectMeetLabelPlural}</p>
                                </div>

                                <div style={{ fontFamily: "'Poppins', sans-serif", background: '#f3f3f3', padding: '30px', borderRadius: '8px' }}>
                                    <div style={{ maxWidth: '676px', margin: '0 auto', background: '#fff', position: 'relative' }}>
                                        {globalRsvpSettings.companyLogoUrl && (
                                            <DraggableLogoPreview
                                                logoUrl={`${environment.sasurl}/${environment.container}/${encodeURIComponent(globalRsvpSettings.companyLogoUrl)}`}
                                                settings={cmLogoSettings}
                                                onUpdateSetting={updateCmLogoSetting}
                                            />
                                        )}
                                        <div style={{ padding: '20px 0 0 0' }}>
                                            <p style={{ fontSize: '20px', width: '80%', margin: '0 auto', fontWeight: 'bold', color: '#393C3D', lineHeight: '24px', display: 'flex', alignItems: 'center' }}>
                                                <span style={{ marginTop: '5px', marginRight: '5px', color: '#f2783c' }}>📍</span>
                                                Here are the groups for {form.title || '{Event Name}'}
                                            </p>
                                        </div>
                                        <div style={{ padding: '5px 0 25px 0' }}>
                                            <p style={{ fontSize: '16px', width: '80%', margin: '0 auto', color: '#6D6D6D', lineHeight: '25px', display: 'flex', alignItems: 'center' }}>
                                                <span style={{ marginTop: '5px', marginRight: '5px', color: '#f2783c' }}>📅</span>
                                                {form.date ? new Date(form.date).toLocaleDateString('en-US', { day: 'numeric', month: 'short', year: 'numeric' }) : '{Event Date}'}
                                            </p>
                                        </div>
                                        <div style={{ padding: '0 10% 20px' }}>
                                            {cmHeaderText ? <div className="ql-editor" style={{ padding: '6px', minHeight: 'auto', color: '#393C3D', lineHeight: '22px', fontSize: '14px' }} dangerouslySetInnerHTML={{ __html: cmHeaderText }} /> : <p style={{ color: '#aaa', fontSize: '14px', margin: 0 }}>Header text (appears above the group tables)</p>}
                                        </div>
                                        <div style={{ width: '80%', margin: '10px auto' }}>
                                            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                                                <thead><tr><th colSpan={2} style={{ padding: '10px 20px', textAlign: 'left', fontSize: '16px', borderTop: '1px solid #cdcdcd', borderLeft: '1px solid #cdcdcd', borderRight: '1px solid #cdcdcd', color: '#272727', fontWeight: '600' }}>Group 1</th></tr></thead>
                                                <tbody>
                                                    <tr><td style={{ padding: '10px 20px', textAlign: 'left', fontSize: '12px', border: '1px solid #cdcdcd', color: '#272727', fontWeight: '600' }}>{'{'}<span>User1 Name</span>{'}'}</td><td style={{ padding: '10px 20px', textAlign: 'left', fontSize: '12px', border: '1px solid #cdcdcd', color: '#272727', fontWeight: '600' }}>{'{'}<span>user1@email.com</span>{'}'}</td></tr>
                                                    <tr><td style={{ padding: '10px 20px', textAlign: 'left', fontSize: '12px', border: '1px solid #cdcdcd', color: '#272727', fontWeight: '600' }}>{'{'}<span>User2 Name</span>{'}'}</td><td style={{ padding: '10px 20px', textAlign: 'left', fontSize: '12px', border: '1px solid #cdcdcd', color: '#272727', fontWeight: '600' }}>{'{'}<span>user2@email.com</span>{'}'}</td></tr>
                                                    <tr><td style={{ padding: '10px 20px', textAlign: 'left', fontSize: '12px', border: '1px solid #cdcdcd', color: '#272727', fontWeight: '600' }}>{'{'}<span>User3 Name</span>{'}'}</td><td style={{ padding: '10px 20px', textAlign: 'left', fontSize: '12px', border: '1px solid #cdcdcd', color: '#272727', fontWeight: '600' }}>{'{'}<span>user3@email.com</span>{'}'}</td></tr>
                                                </tbody>
                                            </table>
                                        </div>
                                        <div style={{ padding: '20px 10% 10px' }}>
                                            {cmFooterText ? <div className="ql-editor" style={{ padding: '6px', minHeight: 'auto', color: '#393C3D', lineHeight: '22px', fontSize: '14px' }} dangerouslySetInnerHTML={{ __html: cmFooterText }} /> : <p style={{ color: '#aaa', fontSize: '14px', margin: 0 }}>Footer text (appears below the group tables)</p>}
                                        </div>
                                        <div style={{ height: '40px' }}></div>
                                        <div style={{ width: '80%', margin: '0 auto', paddingBottom: '30px' }}>
                                            <p style={{ fontSize: '15px', fontWeight: 'bold', color: '#393C3D', lineHeight: '24px' }}>{connectMeetLabel} Manager: {user?.fullName || '{Manager Name}'}{user?.email ? ` (${user.email})` : ''}</p>
                                        </div>
                                    </div>
                                </div>
                            </div>
                            {globalRsvpSettings.companyLogoUrl && (
                                <div className='mt-3 p-3 bg-gray-50 rounded-lg'>
                                    <div className='flex items-center gap-4 flex-wrap'>
                                        <div className='flex items-center gap-2 flex-1 min-w-[200px]'>
                                            <label className='text-sm text-gray-600 whitespace-nowrap'>Size:</label>
                                            <input type="range" min="40" max="400" value={cmLogoSettings.size} onChange={(e) => updateCmLogoSetting('size', parseInt(e.target.value))} className='flex-1' />
                                            <span className='text-sm text-gray-500 w-12'>{cmLogoSettings.size}px</span>
                                        </div>
                                        <div className='flex items-center gap-2 flex-1 min-w-[200px]'>
                                            <label className='text-sm text-gray-600 whitespace-nowrap'>Space below:</label>
                                            <input type="range" min="0" max="100" value={cmLogoSettings.padding} onChange={(e) => updateCmLogoSetting('padding', parseInt(e.target.value))} className='flex-1' />
                                            <span className='text-sm text-gray-500 w-12'>{cmLogoSettings.padding}px</span>
                                        </div>
                                    </div>
                                    <p className='text-xs text-gray-400 mt-1'>Drag the logo in the preview to reposition it</p>
                                </div>
                            )}
                            </>
                        )}
                    </div>

                    <div className="flex justify-end gap-3">
                        {form.id && seriesId && (
                            <button type="button" onClick={handleEditSeries} className="text-white bg-blue-500 hover:bg-blue-600 focus:ring-4 focus:outline-none focus:ring-blue-300 font-medium rounded-lg text-sm px-5 py-2.5 text-center mb-2">
                                Save edits for all series
                            </button>
                        )}
                        <button type="submit" className="text-white bg-orange-400 bg-orange-600 focus:ring-4 focus:outline-none focus:ring-blue-300 dark:focus:ring-blue-800 font-medium rounded-lg text-sm px-5 py-2.5 text-center mb-2">
                            {!form.id && isRecurring ? (recurrence.endType === 'count' ? `Create ${recurrence.endCount} Events` : 'Create Recurring Events') : 'Save'}
                        </button>
                    </div>
                </div>

            </form>
        </Layout>
    </>
}

export default AddEdit